import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Coins, CreditCard, TrendingDown, ShoppingCart,
  Clock, RefreshCw, ChevronDown, Zap, Image, Video, Mic2,
  MessageSquare, Code, Mail, Search, Loader2,
  Music, Box, Globe, Download, FileText, Users
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';
import CreditManagementPage from './CreditManagementPage';
import AutoReloadPanel from '../components/credits/AutoReloadPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  TEXT_CHAT: MessageSquare, IMAGE: Image, VIDEO: Video, AUDIO: Mic2,
  VOICE: Mic2, CODE: Code, EMAIL: Mail, RESEARCH: Search,
  MUSIC: Music, AVATAR: Globe, TALKING_VIDEO: Video, THREE_D: Box,
};
const CATEGORY_COLORS = {
  TEXT_CHAT: '#3B82F6', IMAGE: '#8B5CF6', VIDEO: '#EF4444', AUDIO: '#F59E0B',
  VOICE: '#F97316', CODE: '#10B981', EMAIL: '#06B6D4', RESEARCH: '#6366F1',
  MUSIC: '#EC4899', AVATAR: '#14B8A6', TALKING_VIDEO: '#F43F5E', THREE_D: '#A855F7',
};

// ─── Role-based Top-Up Packages ───
const SUBSCRIBER_TOPUP = [
  { id: 'sub_starter', name: 'Starter Top Up', description: 'For light AI usage', price_usd: 50, base_credits: 5000, bonus_credits: 500, bonus_pct: 10 },
  { id: 'sub_growth', name: 'Growth Top Up', description: 'For regular AI usage', price_usd: 100, base_credits: 10000, bonus_credits: 1500, bonus_pct: 15 },
  { id: 'sub_business', name: 'Business Top Up', description: 'Best value for heavy usage', price_usd: 250, base_credits: 25000, bonus_credits: 7500, bonus_pct: 30 },
];

const OPERATOR_TOPUP = [
  { id: 'op_5k', name: 'Country 5K', description: 'Standard country allocation', price_usd: 5000, base_credits: 500000, bonus_credits: 75000, bonus_pct: 15 },
  { id: 'op_7500', name: 'Country 7.5K', description: 'Extended country allocation', price_usd: 7500, base_credits: 750000, bonus_credits: 150000, bonus_pct: 20 },
  { id: 'op_10k', name: 'Country 10K', description: 'Maximum country allocation', price_usd: 10000, base_credits: 1000000, bonus_credits: 250000, bonus_pct: 25 },
];

const PLATFORM_TOPUP = [
  { id: 'plat_20k', name: 'Platform 20K', description: 'Standard platform replenishment', price_usd: 20000, base_credits: 2000000, bonus_credits: 400000, bonus_pct: 20 },
  { id: 'plat_30k', name: 'Platform 30K', description: 'Extended platform replenishment', price_usd: 30000, base_credits: 3000000, bonus_credits: 750000, bonus_pct: 25 },
  { id: 'plat_50k', name: 'Platform 50K', description: 'Maximum platform replenishment', price_usd: 50000, base_credits: 5000000, bonus_credits: 1500000, bonus_pct: 30 },
];

function getTopUpConfig(role) {
  if (role === 'super_admin') return { packages: PLATFORM_TOPUP, label: 'Platform Top Up', subtitle: 'Replenish platform-wide AI credit pool', source: 'Kaharian API Hub' };
  if (role === 'country_operator') return { packages: OPERATOR_TOPUP, label: 'Country Top Up', subtitle: 'Replenish credits for your country subscriber pool', source: 'Platform allocation' };
  return { packages: SUBSCRIBER_TOPUP, label: 'Top Up Credits', subtitle: 'Purchase additional AI credits when your monthly allocation runs low', source: 'Country Operator' };
}

// ─── Balance Card ───
const BalanceCard = ({ balance, loading, onRefresh }) => {
  if (loading) return <Card className="bg-white border-gray-border"><CardContent className="p-6"><div className="h-24 bg-gray-100 rounded-xl animate-pulse" /></CardContent></Card>;
  const remaining = balance?.remainingCredits ?? 0;
  const hasError = remaining < 0 || balance?.error;
  const total = balance?.totalCredits || 1;
  const used = balance?.usedCredits || 0;
  const ratio = hasError ? 1 : (total > 0 ? remaining / total : 0);
  const statusColor = hasError ? 'amber' : (ratio > 0.3 ? 'emerald' : ratio > 0.1 ? 'amber' : 'red');

  return (
    <Card className="bg-white border-gray-border" data-testid="balance-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", `bg-${statusColor}-100`)}>
              <Coins className={cn("h-6 w-6", `text-${statusColor}-600`)} />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Credit Balance</p>
              <p className="text-3xl font-bold text-text-primary" data-testid="credit-balance">{remaining.toLocaleString()}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="refresh-balance-btn"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Used: <span className="font-medium text-text-primary">{used.toLocaleString()}</span></span>
            <span className="text-gray-500">Total: <span className="font-medium text-text-primary">{total.toLocaleString()}</span></span>
          </div>
          <Progress value={ratio * 100} className="h-2" />
          <Badge className={cn("text-[10px]", `bg-${statusColor}-100 text-${statusColor}-700`)}>
            {hasError ? 'Unavailable' : `${Math.round(ratio * 100)}% remaining`}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Top Up Card ───
const TopUpCard = ({ pkg, onBuy }) => (
  <Card className="bg-white border-gray-border hover:shadow-md transition-shadow" data-testid={`topup-card-${pkg.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">{pkg.name}</h3>
          <p className="text-[11px] text-gray-medium mt-0.5">{pkg.description}</p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">+{pkg.bonus_pct}% bonus</Badge>
      </div>
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-2xl font-bold text-text-primary">${pkg.price_usd.toLocaleString()}</span>
        <span className="text-xs text-gray-400">USD</span>
      </div>
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Base credits</span>
          <span className="font-medium">{pkg.base_credits.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Bonus credits</span>
          <span className="font-medium text-emerald-600">+{pkg.bonus_credits.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between text-xs border-t border-gray-100 pt-1.5">
          <span className="font-medium text-gray-700">Total credits</span>
          <span className="font-bold text-text-primary">{(pkg.base_credits + pkg.bonus_credits).toLocaleString()}</span>
        </div>
      </div>
      <Button className="w-full bg-emerald-brand hover:bg-emerald-600 text-xs" onClick={() => onBuy(pkg)} data-testid={`buy-topup-${pkg.id}`}>
        <ShoppingCart className="h-3.5 w-3.5 mr-1.5" /> Top Up Now
      </Button>
    </CardContent>
  </Card>
);

// ─── Transaction Row ───
const TransactionRow = ({ tx }) => {
  const { format: fc } = useCurrency();
  const isUsage = tx.transaction_type === 'usage' || tx.type === 'usage';
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
      <td className="py-2.5 px-3 text-xs text-gray-500">{new Date(tx.created_at).toLocaleDateString()}<br /><span className="text-[10px] text-gray-400">{new Date(tx.created_at).toLocaleTimeString()}</span></td>
      <td className="py-2.5 px-3"><Badge className={cn("text-[10px]", isUsage ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700")}>{isUsage ? 'Usage' : 'Top Up'}</Badge></td>
      <td className="py-2.5 px-3 text-xs text-gray-700">{tx.action_name || tx.provider || '—'}</td>
      <td className={cn("py-2.5 px-3 text-xs font-medium", isUsage ? "text-red-600" : "text-emerald-600")}>{isUsage ? '-' : '+'}{tx.amount || tx.amount_credits || 0}</td>
      <td className="py-2.5 px-3 text-xs text-gray-500">{tx.amount_usd ? fc(tx.amount_usd) : '—'}</td>
    </tr>
  );
};

// ─── Action Costs Table ───
const ActionCostsSection = ({ data, loading }) => {
  const [expandedCat, setExpandedCat] = useState(null);
  if (loading) return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;
  const baseCosts = data?.base_action_costs || {};
  const providers = data?.providers || {};

  return (
    <Card className="bg-white border-gray-border" data-testid="action-costs-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Credit Cost Reference</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {Object.keys(baseCosts).map((cat) => {
            const info = baseCosts[cat];
            const catProviders = providers[cat] || [];
            const Icon = CATEGORY_ICONS[cat] || Zap;
            const color = CATEGORY_COLORS[cat] || '#6B7280';
            const isExpanded = expandedCat === cat;
            return (
              <div key={cat} className="border border-gray-100 rounded-lg overflow-hidden">
                <button className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left" onClick={() => setExpandedCat(isExpanded ? null : cat)}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}><Icon className="h-3.5 w-3.5" style={{ color }} /></div>
                  <div className="flex-1 min-w-0"><p className="text-xs font-medium text-text-primary">{cat.replace(/_/g, ' ')}</p><p className="text-[10px] text-gray-400">{info.description}</p></div>
                  <Badge variant="outline" className="text-[10px] mr-2">{info.base_credits} cr base</Badge>
                  <span className="text-[10px] text-gray-400">{catProviders.length} providers</span>
                  <ChevronDown className={cn("h-3.5 w-3.5 text-gray-400 transition-transform", isExpanded && "rotate-180")} />
                </button>
                {isExpanded && catProviders.length > 0 && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {catProviders.map((p, i) => (
                        <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-white rounded border border-gray-100">
                          <span className="text-[11px] text-gray-700">{p.name}</span>
                          <Badge variant="outline" className="text-[10px]">{p.credits_per_request} cr</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Page ───
export default function AICreditsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, effectiveRole } = useAuth();
  const initialTab = searchParams.get('tab') || 'usage';

  const role = effectiveRole || user?.role || 'client_admin';
  const config = getTopUpConfig(role);

  const pageTitle = role === 'super_admin' ? 'Platform Top Up' : role === 'country_operator' ? 'Country Top Up' : 'Top Up Credits';

  // v3.16 — team_member cannot purchase credits. Show a friendly notice
  // pointing them to their Subscriber Admin. Any auto-top-up policy lives
  // at the pool-owner (client_admin) level, not per-employee.
  if (role === 'team_member') {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="ai-credits-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Coins className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Credits</h1>
            <p className="text-[11px] text-gray-medium">Your usage — top-ups are handled by your Subscriber Admin.</p>
          </div>
        </div>
        <CreditsUsageView role={role} config={config} />
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="employee-credit-notice">
          <div className="font-semibold mb-1">Need more credits?</div>
          Ask your Subscriber Admin — they can top up your team&apos;s pool from their Credits page.
          You&apos;ll automatically get an alert when your balance drops below 20%.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ai-credits-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Coins className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary" data-testid="ai-credits-title">
            {pageTitle}<PageInfoTooltip pageKey="ai-credits" />
          </h1>
          <p className="text-[11px] text-gray-medium">{config.subtitle}</p>
        </div>
      </div>

      <Tabs defaultValue={initialTab} onValueChange={v => setSearchParams(v === 'usage' ? {} : { tab: v }, { replace: true })}>
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="usage" className="gap-1.5 text-xs" data-testid="credits-tab-usage"><Coins className="h-3.5 w-3.5" />Usage & Balance</TabsTrigger>
          {(role === 'super_admin' || role === 'country_operator') && (
            <TabsTrigger value="management" className="gap-1.5 text-xs" data-testid="credits-tab-management"><Users className="h-3.5 w-3.5" />Allocation</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="usage"><CreditsUsageView role={role} config={config} /></TabsContent>
        {(role === 'super_admin' || role === 'country_operator') && (
          <TabsContent value="management"><CreditManagementPage /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function CreditsUsageView({ role, config }) {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [actionCosts, setActionCosts] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingTx, setLoadingTx] = useState(true);
  const [loadingCosts, setLoadingCosts] = useState(true);
  const [txLimit, setTxLimit] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchBalance = useCallback(async () => {
    setLoadingBalance(true);
    try { const { data } = await axios.get(`${API}/api/credits/balance`); setBalance(data); }
    catch (err) { console.error('Balance fetch error:', err); }
    finally { setLoadingBalance(false); }
  }, []);

  const fetchTransactions = useCallback(async (limit = 20) => {
    try { const { data } = await axios.get(`${API}/api/credits/transactions?limit=${limit}`); setTransactions(data.transactions || []); }
    catch {} finally { setLoadingTx(false); }
  }, []);

  const fetchActionCosts = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/credits/action-costs`); setActionCosts(data); }
    catch {} finally { setLoadingCosts(false); }
  }, []);

  useEffect(() => { fetchBalance(); fetchTransactions(); fetchActionCosts(); }, [fetchBalance, fetchTransactions, fetchActionCosts]);

  const handleBuy = async (pkg) => {
    const currentUrl = window.location.origin;
    // v2.6.38 — Try Kaharian's hosted Stripe checkout first (the historical flow).
    // On "Invalid API key" or any 5xx, fall through to our LOCAL Stripe checkout
    // at /api/payments/credits/topup so the tester can still top up.
    try {
      const { data } = await axios.post(`${API}/api/credits/checkout`, {
        tierId: pkg.id,
        package_id: pkg.id,
        amount: pkg.price_usd,
        credits: pkg.base_credits + pkg.bonus_credits,
        successUrl: `${currentUrl}/settings/credits?status=success`,
        cancelUrl: `${currentUrl}/settings/credits?status=cancelled`,
      });
      if (data?.url) { window.location.href = data.url; return; }
      // Kaharian returned no URL (rare) — fall through to local.
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const status = err?.response?.status;
      // Explicit fallback triggers: Kaharian returned bad key, gateway timeout,
      // or any 5xx / bad-request that means "hosted checkout unavailable".
      const shouldFallback = /invalid api key/i.test(String(detail)) || status >= 500 || status === 400;
      if (!shouldFallback) {
        toast.error(detail || 'Failed to initiate top up');
        return;
      }
    }
    // Local Stripe fallback — uses our STRIPE_API_KEY directly.
    try {
      const { data } = await axios.post(`${API}/api/payments/credits/topup`, {
        package_id: pkg.id,
        origin_url: currentUrl,
        success_url: `${currentUrl}/settings/credits?status=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${currentUrl}/settings/credits?status=cancelled`,
      });
      if (data?.url) { window.location.href = data.url; return; }
      toast.info('Top up request submitted. Credits will be allocated shortly.');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to initiate top up';
      toast.error(String(detail).slice(0, 180));
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    const newLimit = txLimit + 20;
    setTxLimit(newLimit);
    await fetchTransactions(newLimit);
    setLoadingMore(false);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BalanceCard balance={balance} loading={loadingBalance} onRefresh={fetchBalance} />
        <Card className="bg-white border-gray-border">
          <CardContent className="p-5 flex flex-col justify-center h-full">
            <div className="flex items-center gap-2 mb-2"><TrendingDown className="h-4 w-4 text-red-500" /><p className="text-xs text-gray-500 uppercase tracking-wider">Credits Used</p></div>
            <p className="text-2xl font-bold text-text-primary">{Number(balance?.usedCredits || 0).toFixed(1)}</p>
            <p className="text-[10px] text-gray-400 mt-1">{transactions.length} transactions</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-border">
          <CardContent className="p-5 flex flex-col justify-center h-full">
            <div className="flex items-center gap-2 mb-2"><Zap className="h-4 w-4 text-purple-500" /><p className="text-xs text-gray-500 uppercase tracking-wider">Credit Source</p></div>
            <p className="text-sm font-bold text-text-primary">{config.source}</p>
            <p className="text-[10px] text-gray-400 mt-1">
              {role === 'super_admin' ? 'Platform root pool' : role === 'country_operator' ? 'Allocated by platform' : 'Allocated by your operator'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Auto-Reload Panel — subscribers & operators only */}
      {(role === 'client_admin' || role === 'country_operator') && <AutoReloadPanel />}

      {/* Top Up Packages */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">{config.label}</h2>
            <p className="text-[10px] text-gray-400">{config.subtitle}</p>
          </div>
        </div>
        <div className={cn("grid gap-4", config.packages.length <= 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4")}>
          {config.packages.map(pkg => <TopUpCard key={pkg.id} pkg={pkg} onBuy={handleBuy} />)}
        </div>
      </div>

      {/* Transaction History */}
      <Card className="bg-white border-gray-border" data-testid="transactions-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-blue-500" /> Transaction History</CardTitle>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={() => { const a = document.createElement('a'); a.href = `${API}/api/credit-management/export?period=all`; a.download = 'credit_report.csv'; a.click(); }}><Download className="h-3 w-3" />CSV</Button>
              <Button variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={() => { const a = document.createElement('a'); a.href = `${API}/api/credit-management/export/pdf?period=all`; a.download = 'credit_report.pdf'; a.click(); }}><FileText className="h-3 w-3" />PDF</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loadingTx ? <div className="h-40 bg-gray-100 rounded-xl animate-pulse" /> : transactions.length === 0 ? (
            <p className="text-xs text-gray-400 py-6 text-center">No transactions yet</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-gray-100">
                    <th className="text-left text-[10px] text-gray-500 uppercase py-2 px-3">Date</th>
                    <th className="text-left text-[10px] text-gray-500 uppercase py-2 px-3">Type</th>
                    <th className="text-left text-[10px] text-gray-500 uppercase py-2 px-3">Action</th>
                    <th className="text-left text-[10px] text-gray-500 uppercase py-2 px-3">Credits</th>
                    <th className="text-left text-[10px] text-gray-500 uppercase py-2 px-3">Amount</th>
                  </tr></thead>
                  <tbody>{transactions.map((tx, i) => <TransactionRow key={tx.id || i} tx={tx} />)}</tbody>
                </table>
              </div>
              <div className="flex justify-center pt-3">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} className="text-xs">
                  {loadingMore ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}Load More
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ActionCostsSection data={actionCosts} loading={loadingCosts} />
    </div>
  );
}
